import React, { useState } from 'react';
import PropTypes from 'prop-types';
import { SMSFExportEngine } from './SMSFExportEngine';
import jsPDF from 'jspdf';

const SMSFReportingCenter = ({ smsfState }) => {
    const [activeTab, setActiveTab] = useState('generator');
    const [selectedReports, setSelectedReports] = useState([]);
    const [isGenerating, setIsGenerating] = useState(false);
    const [generatingReportName, setGeneratingReportName] = useState('');
    
    // Report Generator (Tests 894-901)
    const [reportType, setReportType] = useState('annual_return');
    const [dateRange, setDateRange] = useState('2024-25');
    const [scheduleFrequency, setScheduleFrequency] = useState('none');

    // Available reports
    const availableReports = [
        {
            id: 'comprehensive',
            name: 'Comprehensive SMSF Report',
            description: 'Complete overview of fund details, contributions, fees, and compliance',
            category: 'General',
            icon: '📋',
            pages: '8-12',
            frequency: 'Annual'
        },
        {
            id: 'fee_analysis',
            name: 'Fee Analysis Report',
            description: 'Detailed breakdown of all fees with industry comparison',
            category: 'Financial',
            icon: '💰',
            pages: '3-5',
            frequency: 'Annual'
        },
        {
            id: 'compliance',
            name: 'Compliance Summary',
            description: 'Contribution caps, TBC, in-house assets, and regulatory compliance',
            category: 'Compliance',
            icon: '✅',
            pages: '4-6',
            frequency: 'Quarterly'
        },
        {
            id: 'lrba',
            name: 'LRBA Analysis Report',
            description: 'Property details, loan structure, cash flow, and compliance',
            category: 'Investment',
            icon: '🏛️',
            pages: '5-7',
            frequency: 'As Needed'
        },
        {
            id: 'division293',
            name: 'Division 293 Tax Assessment',
            description: 'Income calculation, tax liability, and payment options',
            category: 'Tax',
            icon: '💵',
            pages: '3-4',
            frequency: 'Annual'
        },
        {
            id: 'pension',
            name: 'Pension Strategy Report',
            description: 'Pension calculations, projections, and optimization strategies',
            category: 'Retirement',
            icon: '💰',
            pages: '6-8',
            frequency: 'Annual'
        },
        {
            id: 'collectibles',
            name: 'Collectibles Inventory',
            description: 'Complete collectibles register with valuations and insurance',
            category: 'Compliance',
            icon: '🎨',
            pages: '2-4',
            frequency: 'Annual'
        },
        {
            id: 'tax_harvesting',
            name: 'Tax Loss Harvesting Report',
            description: 'Unrealized gains/losses analysis and optimization opportunities',
            category: 'Tax',
            icon: '📉',
            pages: '4-6',
            frequency: 'Quarterly'
        },
        {
            id: 'ato_annual',
            name: 'ATO Annual Return Summary',
            description: 'Pre-filled summary for ATO annual return lodgment',
            category: 'ATO',
            icon: '🏛️',
            pages: '5-7',
            frequency: 'Annual'
        },
        {
            id: 'investment_strategy',
            name: 'Investment Strategy Statement',
            description: 'Required annual investment strategy documentation',
            category: 'Compliance',
            icon: '📊',
            pages: '3-5',
            frequency: 'Annual'
        },
        {
            id: 'trustee_minutes',
            name: 'Trustee Meeting Minutes Template',
            description: 'Template for documenting trustee decisions',
            category: 'Administration',
            icon: '📝',
            pages: '2-3',
            frequency: 'As Needed'
        },
        {
            id: 'member_statements',
            name: 'Member Benefit Statements',
            description: 'Individual member balance and transaction statements',
            category: 'Administration',
            icon: '👤',
            pages: '2-3 per member',
            frequency: 'Annual'
        }
    ];

    // Group reports by category
    const categories = [...new Set(availableReports.map(r => r.category))];

    // Handle report generation
    const handleGenerateReport = (reportId) => {
        try {
            const reportName = availableReports.find(r => r.id === reportId)?.name || reportId;
            
            // Show loading state
            setIsGenerating(true);
            setGeneratingReportName(reportName);
            
            const reportData = prepareReportData(reportId);
            const filename = `${reportId}_${Date.now()}`;
            
            console.log(`Generating report: ${reportName}`);
            
            // Use browser's print dialog for PDF
            const success = SMSFExportEngine.exportToPDF(reportId, reportData, filename);
            
            if (success) {
                console.log('✅ Report generated successfully');
            }
            
            // Hide loading state
            setTimeout(() => {
                setIsGenerating(false);
                setGeneratingReportName('');
            }, 1000);
        } catch (error) {
            console.error('Report generation failed:', error);
            alert(`❌ Failed to generate report: ${error.message}\n\nPlease check the console for details.`);
            setIsGenerating(false);
            setGeneratingReportName('');
        }
    };

    // Handle HTML export
    const handleExportHTML = (reportId) => {
        const reportData = prepareReportData(reportId);
        const filename = `${reportId}_${Date.now()}.html`;
        
        SMSFExportEngine.exportToHTML(reportId, reportData, filename);
    };

    // Handle JSON export
    const handleExportJSON = () => {
        const data = {
            exportDate: new Date().toISOString(),
            financialYear: SMSFExportEngine.getCurrentFinancialYear(),
            fundDetails: smsfState.smsfData,
            memberProfile: smsfState.memberProfile,
            contributions: {
                concessional: smsfState.smsfConcessionalUsed,
                nonConcessional: smsfState.smsfNonConcessionalUsed
            },
            fees: {
                audit: smsfState.smsfAuditFees,
                admin: smsfState.smsfAdminFees,
                accounting: smsfState.smsfAccountingFees
            },
            tbc: {
                your: smsfState.yourTbcUsed,
                partner: smsfState.partnerTbcUsed
            },
            property: {
                hasLRBA: smsfState.smsfDirectProperty,
                value: smsfState.smsfPropertyValue,
                loan: smsfState.smsfPropertyLoan
            }
        };
        
        SMSFExportEngine.exportToJSON(data, `smsf_data_${Date.now()}.json`);
    };

    // Prepare data for specific report
    const prepareReportData = (reportId) => {
        const baseData = {
            fundName: smsfState.smsfData?.fundName,
            abn: smsfState.smsfData?.abn,
            tfn: smsfState.smsfData?.tfn,
            balance: smsfState.smsfData?.balance,
            numberOfMembers: smsfState.smsfData?.numberOfMembers,
            trusteeType: smsfState.smsfData?.trusteeType,
            concessionalUsed: smsfState.smsfConcessionalUsed,
            nonConcessionalUsed: smsfState.smsfNonConcessionalUsed,
            yourTbcUsed: smsfState.yourTbcUsed,
            partnerTbcUsed: smsfState.partnerTbcUsed,
            auditFees: smsfState.smsfAuditFees,
            adminFees: smsfState.smsfAdminFees,
            accountingFees: smsfState.smsfAccountingFees,
            inHouseAssets: smsfState.smsfInHouseAssets,
            hasLRBA: smsfState.smsfDirectProperty,
            propertyValue: smsfState.smsfPropertyValue,
            propertyLoan: smsfState.smsfPropertyLoan,
            propertyInterestRate: smsfState.smsfPropertyInterestRate,
            propertyLoanTerm: smsfState.smsfPropertyLoanTerm,
            rentalIncome: smsfState.smsfPropertyValue * (smsfState.smsfPropertyRentalYield / 100),
            age: smsfState.memberProfile?.age
        };

        return baseData;
    };

    // Toggle report selection
    const toggleReportSelection = (reportId) => {
        if (selectedReports.includes(reportId)) {
            setSelectedReports(selectedReports.filter(id => id !== reportId));
        } else {
            setSelectedReports([...selectedReports, reportId]);
        }
    };

    // Generate multiple reports
    const handleGenerateMultiple = () => {
        selectedReports.forEach((reportId, index) => {
            setTimeout(() => {
                handleGenerateReport(reportId);
            }, index * 500); // Stagger generation
        });
    };

    // Tests 894-901: Report Generator
    const renderGenerator = () => (
        <div className="space-y-6">
            <div className="bg-white p-6 rounded-lg shadow-md">
                <h3 className="text-lg font-semibold mb-4">Generate Report</h3>
                
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    {/* Test 894-896: Report Type */}
                    <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">Report Type</label>
                        <select
                            value={reportType}
                            onChange={(e) => setReportType(e.target.value)}
                            className="w-full px-3 py-2 border border-gray-300 rounded-md"
                        >
                            <option value="annual_return">Annual Return</option>
                            <option value="member_statement">Member Statement</option>
                            <option value="financial_statements">Financial Statements</option>
                            <option value="compliance">Compliance Report</option>
                            <option value="tax_return">Tax Return Summary</option>
                        </select>
                    </div>
                    
                    {/* Test 897: Date Range */}
                    <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">Financial Year</label>
                        <select
                            value={dateRange}
                            onChange={(e) => setDateRange(e.target.value)}
                            className="w-full px-3 py-2 border border-gray-300 rounded-md"
                        >
                            <option value="2024-25">FY 2024-25</option>
                            <option value="2023-24">FY 2023-24</option>
                            <option value="2022-23">FY 2022-23</option>
                            <option value="2021-22">FY 2021-22</option>
                        </select>
                    </div>
                </div>
                
                {/* Test 898: Generate Button */}
                <div className="mt-6">
                    <button
                        onClick={() => {
                            // Map the dropdown report types to actual report IDs
                            const reportMapping = {
                                'annual_return': 'ato_annual',
                                'member_statement': 'member_statements',
                                'financial_statements': 'comprehensive',
                                'compliance': 'compliance',
                                'tax_return': 'division293'
                            };
                            
                            const actualReportId = reportMapping[reportType] || 'comprehensive';
                            handleGenerateReport(actualReportId);
                        }}
                        className="w-full bg-blue-600 text-white py-3 rounded-lg hover:bg-blue-700 font-semibold"
                    >
                        📄 Generate Report
                    </button>
                </div>
                
                {/* Individual tool exports removed - use comprehensive SMSF PDF export at top of page */}
                
                {/* Test 901: Schedule Report */}
                <div className="mt-6 p-4 bg-gray-50 rounded-lg">
                    <label className="block text-sm font-medium text-gray-700 mb-2">Schedule Report</label>
                    <select
                        value={scheduleFrequency}
                        onChange={(e) => {
                            setScheduleFrequency(e.target.value);
                            if (e.target.value !== 'none') {
                                alert(`✅ ${e.target.value} reminder created for ${reportType.replace(/_/g, ' ')}`);
                            }
                        }}
                        className="w-full px-3 py-2 border border-gray-300 rounded-md"
                    >
                        <option value="none">No Schedule</option>
                        <option value="Monthly">Monthly</option>
                        <option value="Quarterly">Quarterly</option>
                        <option value="Annually">Annually</option>
                    </select>
                </div>
            </div>
        </div>
    );
    
    const renderOverview = () => (
        <div className="space-y-6">
            {/* Report Categories */}
            <div className="bg-white p-6 rounded-lg shadow-md">
                <h3 className="text-lg font-semibold mb-4">Available Reports by Category</h3>
                {categories.map(category => {
                    const categoryReports = availableReports.filter(r => r.category === category);
                    
                    return (
                        <div key={category} className="mb-6">
                            <h4 className="font-semibold text-gray-700 mb-3">{category} Reports</h4>
                            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                                {categoryReports.map(report => (
                                    <div key={report.id} className="border border-gray-200 rounded-lg p-4 hover:border-blue-500 transition-colors">
                                        <div className="flex items-start mb-2">
                                            <span className="text-2xl mr-2">{report.icon}</span>
                                            <div className="flex-1">
                                                <h5 className="font-semibold text-gray-800">{report.name}</h5>
                                                <p className="text-xs text-gray-600 mt-1">{report.description}</p>
                                            </div>
                                        </div>
                                        <div className="flex justify-between items-center text-xs text-gray-500 mt-3">
                                            <span>{report.pages} pages</span>
                                            <span>{report.frequency}</span>
                                        </div>
                                        <div className="flex space-x-2 mt-3">
                                            <button
                                                onClick={() => handleGenerateReport(report.id)}
                                                className="flex-1 px-3 py-1 bg-blue-600 text-white text-sm rounded hover:bg-blue-700"
                                            >
                                                Generate PDF
                                            </button>
                                            <button
                                                onClick={() => handleExportHTML(report.id)}
                                                className="px-3 py-1 bg-gray-600 text-white text-sm rounded hover:bg-gray-700"
                                            >
                                                HTML
                                            </button>
                                        </div>
                                    </div>
                                ))}
                            </div>
                        </div>
                    );
                })}
            </div>

            {/* Quick Actions */}
            <div className="bg-white p-6 rounded-lg shadow-md">
                <h3 className="text-lg font-semibold mb-4">Quick Export Actions</h3>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                    <button
                        onClick={() => handleGenerateReport('comprehensive')}
                        className="p-4 bg-blue-50 border-2 border-blue-500 rounded-lg hover:bg-blue-100 transition-colors"
                    >
                        <div className="text-3xl mb-2">📋</div>
                        <div className="font-semibold text-gray-800">Generate Full Report</div>
                        <div className="text-sm text-gray-600">All SMSF details</div>
                    </button>
                    <button
                        onClick={() => handleGenerateReport('ato_annual')}
                        className="p-4 bg-green-50 border-2 border-green-500 rounded-lg hover:bg-green-100 transition-colors"
                    >
                        <div className="text-3xl mb-2">🏛️</div>
                        <div className="font-semibold text-gray-800">ATO Annual Return</div>
                        <div className="text-sm text-gray-600">For tax lodgment</div>
                    </button>
                    <button
                        onClick={handleExportJSON}
                        className="p-4 bg-purple-50 border-2 border-purple-500 rounded-lg hover:bg-purple-100 transition-colors"
                    >
                        <div className="text-3xl mb-2">💾</div>
                        <div className="font-semibold text-gray-800">Export Data</div>
                        <div className="text-sm text-gray-600">JSON backup</div>
                    </button>
                </div>
            </div>
        </div>
    );

    const renderBulkExport = () => (
        <div className="space-y-6">
            {/* Bulk Selection */}
            <div className="bg-white p-6 rounded-lg shadow-md">
                <h3 className="text-lg font-semibold mb-4">Select Reports for Bulk Export</h3>
                <div className="space-y-2">
                    {availableReports.map(report => (
                        <div key={report.id} className="flex items-center p-3 border border-gray-200 rounded-lg hover:bg-gray-50">
                            <input
                                type="checkbox"
                                checked={selectedReports.includes(report.id)}
                                onChange={() => toggleReportSelection(report.id)}
                                className="mr-3 h-4 w-4 text-blue-600"
                            />
                            <span className="text-2xl mr-3">{report.icon}</span>
                            <div className="flex-1">
                                <div className="font-medium text-gray-800">{report.name}</div>
                                <div className="text-sm text-gray-600">{report.description}</div>
                            </div>
                            <div className="text-xs text-gray-500 text-right">
                                <div>{report.category}</div>
                                <div>{report.pages} pages</div>
                            </div>
                        </div>
                    ))}
                </div>

                {selectedReports.length > 0 && (
                    <div className="mt-6 p-4 bg-blue-50 rounded-lg">
                        <div className="flex justify-between items-center">
                            <div>
                                <p className="font-semibold text-gray-800">
                                    {selectedReports.length} report(s) selected
                                </p>
                                <p className="text-sm text-gray-600">
                                    Reports will be generated sequentially
                                </p>
                            </div>
                            <div className="flex space-x-2">
                                <button
                                    onClick={() => setSelectedReports([])}
                                    className="px-4 py-2 bg-gray-600 text-white rounded-md hover:bg-gray-700"
                                >
                                    Clear Selection
                                </button>
                                <button
                                    onClick={handleGenerateMultiple}
                                    className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700"
                                >
                                    Generate All PDFs
                                </button>
                            </div>
                        </div>
                    </div>
                )}
            </div>

            {/* Quick Select Presets */}
            <div className="bg-white p-6 rounded-lg shadow-md">
                <h3 className="text-lg font-semibold mb-4">Quick Select Presets</h3>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                    <button
                        onClick={() => setSelectedReports(['comprehensive', 'compliance', 'ato_annual'])}
                        className="p-4 border-2 border-blue-500 rounded-lg hover:bg-blue-50"
                    >
                        <div className="font-semibold text-gray-800">Annual Package</div>
                        <div className="text-sm text-gray-600 mt-1">Essential annual reports</div>
                    </button>
                    <button
                        onClick={() => setSelectedReports(['fee_analysis', 'compliance', 'investment_strategy'])}
                        className="p-4 border-2 border-green-500 rounded-lg hover:bg-green-50"
                    >
                        <div className="font-semibold text-gray-800">Compliance Package</div>
                        <div className="text-sm text-gray-600 mt-1">For audit preparation</div>
                    </button>
                    <button
                        onClick={() => setSelectedReports(['pension', 'tax_harvesting', 'division293'])}
                        className="p-4 border-2 border-purple-500 rounded-lg hover:bg-purple-50"
                    >
                        <div className="font-semibold text-gray-800">Tax Planning Package</div>
                        <div className="text-sm text-gray-600 mt-1">Tax optimization reports</div>
                    </button>
                </div>
            </div>
        </div>
    );

    const renderScheduled = () => (
        <div className="space-y-6">
            {/* Scheduled Reports */}
            <div className="bg-white p-6 rounded-lg shadow-md">
                <h3 className="text-lg font-semibold mb-4">Reporting Schedule</h3>
                <div className="space-y-4">
                    {[
                        { period: 'Quarterly', reports: ['compliance', 'tax_harvesting'], icon: '📅', color: 'blue' },
                        { period: 'Annually', reports: ['comprehensive', 'ato_annual', 'fee_analysis', 'investment_strategy'], icon: '📆', color: 'green' },
                        { period: 'As Needed', reports: ['lrba', 'division293', 'pension', 'collectibles'], icon: '🔔', color: 'purple' }
                    ].map((schedule, index) => (
                        <div key={index} className={`border-l-4 border-${schedule.color}-500 bg-${schedule.color}-50 p-4 rounded-r-lg`}>
                            <div className="flex items-center mb-2">
                                <span className="text-2xl mr-3">{schedule.icon}</span>
                                <h4 className="font-semibold text-gray-800">{schedule.period} Reports</h4>
                            </div>
                            <div className="flex flex-wrap gap-2">
                                {schedule.reports.map(reportId => {
                                    const report = availableReports.find(r => r.id === reportId);
                                    return report ? (
                                        <span key={reportId} className="px-2 py-1 bg-white border border-gray-300 rounded text-sm">
                                            {report.icon} {report.name}
                                        </span>
                                    ) : null;
                                })}
                            </div>
                        </div>
                    ))}
                </div>
            </div>

            {/* Next Due Dates */}
            <div className="bg-white p-6 rounded-lg shadow-md">
                <h3 className="text-lg font-semibold mb-4">Upcoming Report Deadlines</h3>
                <table className="w-full">
                    <thead>
                        <tr className="border-b border-gray-200">
                            <th className="text-left py-3 px-4">Report</th>
                            <th className="text-left py-3 px-4">Due Date</th>
                            <th className="text-left py-3 px-4">Priority</th>
                            <th className="text-center py-3 px-4">Action</th>
                        </tr>
                    </thead>
                    <tbody>
                        <tr className="border-b border-gray-100">
                            <td className="py-3 px-4">ATO Annual Return</td>
                            <td className="py-3 px-4">28 February 2026</td>
                            <td className="py-3 px-4">
                                <span className="px-2 py-1 bg-red-100 text-red-800 text-xs rounded">Critical</span>
                            </td>
                            <td className="py-3 px-4 text-center">
                                <button
                                    onClick={() => handleGenerateReport('ato_annual')}
                                    className="px-3 py-1 bg-blue-600 text-white text-sm rounded hover:bg-blue-700"
                                >
                                    Generate
                                </button>
                            </td>
                        </tr>
                        <tr className="border-b border-gray-100">
                            <td className="py-3 px-4">Investment Strategy Review</td>
                            <td className="py-3 px-4">30 June 2025</td>
                            <td className="py-3 px-4">
                                <span className="px-2 py-1 bg-orange-100 text-orange-800 text-xs rounded">High</span>
                            </td>
                            <td className="py-3 px-4 text-center">
                                <button
                                    onClick={() => handleGenerateReport('investment_strategy')}
                                    className="px-3 py-1 bg-blue-600 text-white text-sm rounded hover:bg-blue-700"
                                >
                                    Generate
                                </button>
                            </td>
                        </tr>
                        <tr className="border-b border-gray-100">
                            <td className="py-3 px-4">Quarterly Compliance Check</td>
                            <td className="py-3 px-4">31 December 2025</td>
                            <td className="py-3 px-4">
                                <span className="px-2 py-1 bg-yellow-100 text-yellow-800 text-xs rounded">Medium</span>
                            </td>
                            <td className="py-3 px-4 text-center">
                                <button
                                    onClick={() => handleGenerateReport('compliance')}
                                    className="px-3 py-1 bg-blue-600 text-white text-sm rounded hover:bg-blue-700"
                                >
                                    Generate
                                </button>
                            </td>
                        </tr>
                    </tbody>
                </table>
            </div>
        </div>
    );

    return (
        <div className="space-y-6 p-6 bg-gray-50 rounded-lg shadow-inner">
            <div className="flex items-center justify-between">
                <h2 className="text-2xl font-bold text-gray-800">Reporting Center</h2>
                <div className="flex space-x-2">
                    <button
                        onClick={() => setActiveTab('generator')}
                        className={`px-4 py-2 rounded-md transition-colors ${
                            activeTab === 'generator' 
                                ? 'bg-blue-600 text-white' 
                                : 'bg-white text-gray-700 hover:bg-gray-50'
                        }`}
                    >
                        Generate
                    </button>
                    <button
                        onClick={() => setActiveTab('overview')}
                        className={`px-4 py-2 rounded-md transition-colors ${
                            activeTab === 'overview' 
                                ? 'bg-blue-600 text-white' 
                                : 'bg-white text-gray-700 hover:bg-gray-50'
                        }`}
                    >
                        Overview
                    </button>
                    <button
                        onClick={() => setActiveTab('bulk')}
                        className={`px-4 py-2 rounded-md transition-colors ${
                            activeTab === 'bulk' 
                                ? 'bg-blue-600 text-white' 
                                : 'bg-white text-gray-700 hover:bg-gray-50'
                        }`}
                    >
                        Bulk Export
                    </button>
                    <button
                        onClick={() => setActiveTab('schedule')}
                        className={`px-4 py-2 rounded-md transition-colors ${
                            activeTab === 'schedule' 
                                ? 'bg-blue-600 text-white' 
                                : 'bg-white text-gray-700 hover:bg-gray-50'
                        }`}
                    >
                        Schedule
                    </button>
                </div>
            </div>

            {activeTab === 'generator' && renderGenerator()}
            {activeTab === 'overview' && renderOverview()}
            {activeTab === 'bulk' && renderBulkExport()}
            {activeTab === 'schedule' && renderScheduled()}

            {/* Action Buttons */}
            <div className="bg-white p-6 rounded-lg shadow-md">
                <div className="flex justify-between items-center">
                    <div className="text-sm text-gray-600">
                        Reports generated on: {new Date().toLocaleDateString()}
                    </div>
                    <div className="flex space-x-4">
                        <button 
                            onClick={handleExportJSON}
                            className="px-4 py-2 bg-purple-600 text-white rounded-md hover:bg-purple-700 transition-colors"
                        >
                            Export All Data (JSON)
                        </button>
                        <button className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 transition-colors">
                            Email Reports
                        </button>
                    </div>
                </div>
            </div>
        </div>
    );
};

SMSFReportingCenter.propTypes = {
    smsfState: PropTypes.object.isRequired
};

export default SMSFReportingCenter;
